import {
  type BillingMachineResource,
  BillingMachinesController_list_v1,
  type OutletResource,
  OutletsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { useSession } from '@/auth/session';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { isRetired } from '../outlets/outlet-kinds';
import { billingMachinePath } from '../paths';
import { formatBusinessDate } from '../tickets/business-date';
import { RegisterMachineDialog } from './RegisterMachineDialog';

/**
 * The stand-alone billing machines at the ride and activity counters (#228).
 *
 * A machine prints bills that never touch a till; its `ORDERS.CSV` is uploaded
 * later and each bill becomes a cash sale at the outlet the machine was attached
 * to **on the bill's own date**. So the outlet column here is "where it sells
 * today", and the full, dated history is on the machine's own record.
 *
 * Read whole, like terminals: the set is bounded by the park's counters, not by
 * use. Registering is owner-only on the API, so a manager is not shown the
 * button — the read is theirs either way.
 */

type LoadState<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly rows: readonly T[] }
  | { readonly kind: 'failed'; readonly error: unknown };

const EVERY_OUTLET = 'ALL';
const COLUMNS = 4;
const NO_OUTLETS: readonly OutletResource[] = [];

export function BillingMachines() {
  const client = useApiClient();
  const { session } = useSession();

  // Courtesy, not security: the role is editable in `sessionStorage` and the
  // API refuses a manager's registration regardless. The dialog words that.
  const mayRegister = session?.user.role === 'OWNER';

  const [outletFilter, setOutletFilter] = useState<string>(EVERY_OUTLET);
  const [outlets, setOutlets] = useState<LoadState<OutletResource>>({ kind: 'loading' });
  const [machines, setMachines] = useState<LoadState<BillingMachineResource>>({
    kind: 'loading',
  });
  const [registering, setRegistering] = useState(false);
  const [justRegistered, setJustRegistered] = useState<BillingMachineResource | null>(null);

  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setOutlets({ kind: 'loading' });

    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        // Retired included for the filter: a machine may still stand in one.
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((rows) => {
        if (live) setOutlets({ kind: 'loaded', rows });
      })
      .catch((error: unknown) => {
        if (live) setOutlets({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setMachines({ kind: 'loading' });

    collectPages<BillingMachineResource>((offset, limit) =>
      client.request(BillingMachinesController_list_v1, {
        // Filtered at the API, which reads "currently attached to this outlet".
        query: {
          offset,
          limit,
          ...(outletFilter === EVERY_OUTLET ? {} : { outletId: outletFilter }),
        },
        signal: controller.signal,
      }),
    )
      .then((rows) => {
        if (live) setMachines({ kind: 'loaded', rows });
      })
      .catch((error: unknown) => {
        if (live) setMachines({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletFilter, reloads]);

  const outletRows = useMemo(
    () => (outlets.kind === 'loaded' ? outlets.rows : NO_OUTLETS),
    [outlets],
  );

  const outletFilterItems = useMemo(
    () => [
      { label: 'All outlets', value: EVERY_OUTLET },
      ...outletRows.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outletRows],
  );

  const openOutlets = useMemo(
    () => outletRows.filter((outlet) => !isRetired(outlet)),
    [outletRows],
  );

  // The machines carry their outlet's name, so only the machine read fails the
  // screen. A failed outlet read costs the filter, which is disabled.
  const failure = machines.kind === 'failed' ? machines : null;
  const loading = machines.kind === 'loading';

  return (
    <Screen
      title="Billing machines"
      lede="The counters that print their own bills"
      description="Stand-alone billing machines at the ride and activity counters. Their exports are uploaded as cash sales, each bill booked to the outlet the machine was attached to on the bill's own date."
      action={
        mayRegister ? (
          <Button onClick={() => setRegistering(true)}>
            <IconPlus />
            Register machine
          </Button>
        ) : undefined
      }
      aside={
        <>
          {machines.kind === 'loaded' && (
            <ScreenFact label="Showing">
              <span className="font-mono numeric">{machines.rows.length}</span>{' '}
              {machines.rows.length === 1 ? 'machine' : 'machines'}
            </ScreenFact>
          )}

          <div className="space-y-2">
            <Label htmlFor="machine-outlet-filter" className="text-sm">
              Outlet
            </Label>
            <Select
              value={outletFilter}
              onValueChange={(next) => setOutletFilter(next ?? EVERY_OUTLET)}
              items={outletFilterItems}
              disabled={outlets.kind !== 'loaded'}
            >
              <SelectTrigger id="machine-outlet-filter" aria-label="Outlet" className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={EVERY_OUTLET}>All outlets</SelectItem>
                  {outletRows.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </>
      }
    >
      {justRegistered !== null && (
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>
            <span className="font-mono text-foreground numeric">{justRegistered.machineCode}</span>{' '}
            — {justRegistered.name} has been registered. Map its product codes before uploading its
            first export.
          </AlertDescription>
        </Alert>
      )}

      <ScreenTable label="Billing machines">
        <TableHeader>
          <TableRow>
            <TableHead className="w-44">Machine ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-56">Outlet</TableHead>
            <TableHead className="w-40">Since</TableHead>
          </TableRow>
        </TableHeader>

        {loading && <TableSkeleton columns={COLUMNS} />}

        {failure !== null && (
          <TableNotice columns={COLUMNS}>
            <LoadFailed error={failure.error} onRetry={reload} />
          </TableNotice>
        )}

        {machines.kind === 'loaded' &&
          (machines.rows.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="No billing machines yet"
                description={
                  outletFilter !== EVERY_OUTLET
                    ? 'No machine sells for this outlet today. Choose All outlets to see the rest of the park.'
                    : mayRegister
                      ? 'A billing machine is one counter that prints its own bills. Register machine adds the first, with the machine ID its export prints.'
                      : 'A billing machine is one counter that prints its own bills. An owner registers each one, with the machine ID its export prints.'
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {machines.rows.map((machine) => (
                <MachineRow key={machine.id} machine={machine} />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {mayRegister && (
        <RegisterMachineDialog
          open={registering}
          onOpenChange={setRegistering}
          outlets={openOutlets}
          defaultOutletId={outletFilter === EVERY_OUTLET ? null : outletFilter}
          onRegistered={(machine) => {
            setRegistering(false);
            setJustRegistered(machine);
            reload();
          }}
        />
      )}
    </Screen>
  );
}

function MachineRow({ machine }: { machine: BillingMachineResource }) {
  const current = machine.currentAttachment;

  return (
    <TableRow>
      <TableCell>
        {/* The machine ID is text read off the export and typed back in: mono,
            tabular, and never parsed — its leading zeros are part of it. */}
        <Link
          to={billingMachinePath(machine.id)}
          aria-label={`The record for machine ${machine.machineCode}`}
          className="rounded-sm font-mono text-foreground numeric underline decoration-border decoration-dotted underline-offset-4 outline-none hover:decoration-current focus-visible:ring-3 focus-visible:ring-ring/30"
        >
          {machine.machineCode}
        </Link>
      </TableCell>
      <TableCell className="text-foreground">{machine.name}</TableCell>
      <TableCell>
        {current === null ? (
          <span className="text-muted-foreground">Not attached yet</span>
        ) : current.outletName === null ? (
          <span className="text-muted-foreground">Attached to nothing</span>
        ) : (
          current.outletName
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap numeric">
        {current === null ? '' : formatBusinessDate(current.effectiveFrom)}
      </TableCell>
    </TableRow>
  );
}
